import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { DropletIcon, LeafIcon, BarChart3Icon, ShieldIcon } from 'lucide-react';
import useTranslation from '@/hooks/use-translation';
import { useLanguage } from '@/contexts/LanguageContext';
import { cn } from '@/lib/utils';

const Landing = () => {
  const { translate } = useTranslation();
  const { isRTL } = useLanguage();

  return (
    <div className={cn(
      "min-h-screen relative",
      isRTL && "rtl"
    )}>
      {/* Background Image with Overlay */}
      <div 
        className="fixed inset-0 z-0 bg-cover bg-center bg-no-repeat" 
        style={{
          backgroundImage: 'url(/images/water-bg.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          height: '100vh',
          width: '100vw'
        }}
      />
      <div className="fixed inset-0 bg-black/30 z-10" />

      {/* Content */}
      <div className="relative z-20 min-h-screen flex flex-col">
        {/* Hero Section */}
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center px-4 max-w-5xl mx-auto">
            <div className="flex justify-center mb-8">
              <img 
                src="/images/logo.png" 
                alt="AquaGenius Logo" 
                className="h-32 w-32 object-contain animate-float"
              />
            </div>
            <h1 className="text-4xl md:text-6xl font-bold mb-6 text-white">
              {translate("Welcome to AQUAGENIUS")}
            </h1>
            <p className="text-xl md:text-2xl mb-12 text-gray-200">
              {translate("Smart Water Management Solution for a Sustainable Future")}
            </p>
            <div className={cn(
              "flex flex-col sm:flex-row gap-4 justify-center",
              isRTL && "flex-row-reverse"
            )}>
              <Button
                asChild
                size="lg"
                className="bg-blue-600 hover:bg-blue-700 text-lg px-8"
              >
                <Link to="/login">
                  {translate("Get Started")}
                </Link>
              </Button>
              <Button
                asChild
                size="lg"
                variant="outline"
                className="bg-white/10 border-2 text-white border-white hover:bg-white/20 text-lg px-8"
              >
                <Link to="/register">
                  {translate("Sign Up")}
                </Link>
              </Button>
            </div>
          </div>
        </div>

        {/* Features Section */}
        <div className="py-20 px-4 bg-white/95 dark:bg-black/95">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-center mb-12">
              {translate("Why Choose AQUAGENIUS?")}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              <FeatureCard
                icon={<DropletIcon className="h-8 w-8 text-blue-500" />}
                title={translate("Smart Water Management")}
                description={translate("Real-time monitoring and AI-powered insights for optimal water usage.")}
              />
              <FeatureCard
                icon={<LeafIcon className="h-8 w-8 text-green-500" />}
                title={translate("Sustainable Solutions")}
                description={translate("Eco-friendly approaches to water conservation for both agriculture and household use.")}
              />
              <FeatureCard
                icon={<BarChart3Icon className="h-8 w-8 text-purple-500" />}
                title={translate("Advanced Analytics")}
                description={translate("Detailed analytics and reporting to help you make informed decisions.")}
              />
              <FeatureCard
                icon={<ShieldIcon className="h-8 w-8 text-amber-500" />}
                title={translate("Secure & Reliable")}
                description={translate("Enterprise-grade security and reliability for your water management needs.")}
              />
            </div>
          </div>
        </div>

        {/* Call to Action Section */}
        <div className="py-20 px-4 bg-gradient-to-r from-blue-600/90 to-green-600/90 text-white relative">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold mb-6">
              {translate("Ready to Optimize Your Water Usage?")}
            </h2>
            <p className="text-xl mb-8 text-blue-100">
              {translate("Join thousands of users who are making a difference with AQUAGENIUS.")}
            </p>
            <Button
              asChild
              size="lg"
              className="bg-white text-blue-600 hover:bg-blue-50 text-lg px-8"
            >
              <Link to="/register">
                {translate("Start Your Journey")}
              </Link>
            </Button>
          </div>
        </div>

        {/* Footer */}
        <footer className="bg-gray-900/95 text-white py-8">
          <div className="max-w-6xl mx-auto px-4 text-center">
            <div className={cn(
              "flex justify-center items-center gap-2 mb-4",
              isRTL && "flex-row-reverse"
            )}>
              <img 
                src="/images/logo.png" 
                alt="AquaGenius" 
                className="h-8 w-8 object-contain" 
              />
              <span className="font-semibold text-xl">AQUAGENIUS</span>
            </div>
            <p className="text-gray-400">
              © {new Date().getFullYear()} AquaGenius. {translate("All rights reserved.")}
            </p>
          </div>
        </footer>
      </div>
    </div>
  );
};

const FeatureCard = ({ icon, title, description }: { 
  icon: React.ReactNode; 
  title: string; 
  description: string; 
}) => {
  return (
    <div className="p-6 rounded-xl bg-white dark:bg-gray-800 shadow-lg hover:shadow-xl transition-shadow duration-300">
      <div className="mb-4 flex justify-center">{icon}</div>
      <h3 className="text-xl font-semibold mb-3 text-center">{title}</h3>
      <p className="text-gray-600 dark:text-gray-300 text-center">{description}</p>
    </div>
  );
};

// Add floating animation
const styles = `
  @keyframes float {
    0% { transform: translateY(0px); }
    50% { transform: translateY(-10px); }
    100% { transform: translateY(0px); }
  }
  .animate-float {
    animation: float 3s ease-in-out infinite;
  }
`;

const styleSheet = document.createElement("style");
styleSheet.textContent = styles;
document.head.appendChild(styleSheet);

export default Landing;